import { Link } from "react-router-dom";
import { listCapabilities, listGuardrails, listMaps, listStandards, listTools } from "@/api/endpoints";
import { useAsync } from "@/lib/useAsync";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { updatedLabel } from "@/lib/time";
import { HowItFitsTogether } from "@/components/HowItFitsTogether";

/** Everything the cards count, fetched in parallel; any one failing leaves the cards without
 * their numbers (the descriptions still render). */
async function loadDashboard() {
  const [capabilities, standards, tools, maps, guardrails] = await Promise.all([
    listCapabilities(),
    listStandards(),
    listTools(),
    listMaps(),
    listGuardrails(),
  ]);
  return { capabilities, standards, tools, maps, guardrails };
}

type Dashboard = Awaited<ReturnType<typeof loadDashboard>>;

/** One orientation card: what the page is for, in a sentence, and its headline number. */
interface PageCard {
  to: string;
  label: string;
  description: string;
  /** The card's count and what it counts, from the loaded catalog; absent until it loads. */
  stat: (d: Dashboard) => { value: string; sub: string };
}

/** The cards in the header menu's two groups: the catalog — what Guild's platform has and
 * expects — and changes, which move through their pages in order (a change is mapped, reviewed,
 * then becomes a delivery brief), so that group is drawn as a flow. */
const GROUPS: { label: string; blurb: string; flow?: boolean; cards: PageCard[] }[] = [
  {
    label: "Catalog",
    blurb: "What Guild's platform has and expects. Curated data, read and corrected here.",
    cards: [
      {
        to: "/registry",
        label: "Platform Capabilities",
        description:
          "The capabilities themselves: what each one owns, its inputs/outputs and boundaries, what it draws from and feeds, and a plain-language summary to share before a planning session.",
        stat: (d) => ({
          value: `${d.capabilities.capabilities.filter((c) => c.status === "active").length} / ${d.capabilities.capabilities.length}`,
          sub: "capabilities, active / total",
        }),
      },
      {
        to: "/standards",
        label: "Engineering Standards and Practices",
        description:
          "What to do: the engineering standards a delivery team designs against, graded by how settled each is, beside the Tech Radar's recognised practices for meeting them.",
        stat: (d) => ({
          value: String(d.standards.standards.length),
          sub: `standards, ${updatedLabel(d.standards.last_updated).text}`,
        }),
      },
      {
        to: "/tools",
        label: "Tools",
        description:
          "What to use for X: tools, platforms and libraries rated by the Tech Radar, linked to Trelica and to the standards they meet, with anything that needs attention flagged.",
        stat: (d) => ({
          value: String(d.tools.tools.length),
          sub: `tools, ${updatedLabel(d.tools.last_updated).text}`,
        }),
      },
    ],
  },
  {
    label: "Changes",
    blurb: "A change is mapped, then reviewed by a Staff+ engineer, then becomes a brief for the team.",
    flow: true,
    cards: [
      {
        to: "/map",
        label: "Map a change",
        description:
          "Paste a product description and get back the capabilities it touches, the standards it implies, and the tools and practices that could help.",
        stat: (d) => ({ value: String(d.maps.maps.length), sub: "maps produced, all-time" }),
      },
      {
        to: "/review",
        label: "Review",
        description: "The Staff+ pass over a suggested map: accept, reject, or add matches, then sign off.",
        stat: (d) => ({
          value: String(d.maps.maps.filter((m) => m.review_status !== "signed-off").length),
          sub: "maps awaiting sign-off",
        }),
      },
      {
        to: "/briefs",
        label: "Delivery briefs",
        description:
          "Signed-off maps, each a brief for the team building the change: boundaries to respect, what's in scope, the standards and tools that apply, and a markdown copy for a ticket.",
        stat: (d) => ({ value: String(d.guardrails.maps.length), sub: "signed-off briefs" }),
      },
    ],
  },
];

/** The landing page: what the tool does and who does what (a Staff+ engineer signs off a
 * suggested map; a delivery team works from its delivery brief), then a card per page, grouped
 * as the header menu is, each with its headline count. */
export function HomePage() {
  const state = useAsync(loadDashboard, []);

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="font-[family-name:--font-display] text-2xl">Welcome to the Capability Registry</h2>
        <p className="mt-2 max-w-3xl leading-relaxed text-text/80">
          This tool takes a product requirement and returns the Guild Platform capabilities it touches, the engineering
          standards the team building it should design against, and the tools and practices that could help, including
          Trial and Assess ones otherwise found only through the right hallway conversation. A Staff+ engineer reviews
          and signs off on the suggested answer; a delivery team works from the signed-off result, its delivery brief.
          The registry itself (what each capability owns, and how it differs from its neighbors) is curated data, not a
          static doc, so it gets more accurate the more it's used and corrected.
        </p>
      </div>

      <HowItFitsTogether />

      <div className="flex flex-col gap-6">
        <h3 className="text-xs font-semibold tracking-wide text-text/60 uppercase">How to use this</h3>
        {state.status === "error" && <p className="text-warn">Couldn't load the counts: {state.message}</p>}
        {GROUPS.map((g) => (
          <section key={g.label} aria-label={g.label}>
            <div className="mb-2 flex items-baseline gap-3">
              <h4 className="font-[family-name:--font-display] text-lg">{g.label}</h4>
              <p className="text-sm text-text/60">{g.blurb}</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {g.cards.map((c, i) => {
                const stat = state.status === "ready" ? c.stat(state.data) : null;
                return (
                  <div key={c.to} className="relative">
                    {g.flow && i > 0 && (
                      <span
                        aria-hidden="true"
                        className="absolute top-1/2 -left-3 z-10 -translate-x-1/2 -translate-y-1/2 text-lg text-text/40"
                      >
                        ›
                      </span>
                    )}
                    <Link to={c.to}>
                      <Card className="h-full hover:border-accent-line">
                        <div className="flex items-baseline justify-between gap-3">
                          <h5 className="font-medium">{c.label}</h5>
                          {stat && (
                            <span className="font-[family-name:--font-display] text-2xl leading-none">
                              {stat.value}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 h-4 text-right text-xs text-text/50">{stat?.sub}</p>
                        <p className="mt-1 text-sm text-text/70">{c.description}</p>
                      </Card>
                    </Link>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
        {state.status === "ready" && <MostRecentAsk map={state.data.maps.maps[0]} />}
      </div>
    </div>
  );
}

/** The newest map, linked to its review — under the Changes cards, as where a change stands now. */
function MostRecentAsk({ map }: { map: Dashboard["maps"]["maps"][number] | undefined }) {
  if (!map) return null;
  return (
    <Card>
      <h3 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">Most recent ask</h3>
      <div className="flex items-center gap-3">
        <Link to={`/review/${encodeURIComponent(map.id)}`} className="font-medium text-accent hover:underline">
          {map.title}
        </Link>
        <Badge>{map.verdict}</Badge>
        <span className="text-sm text-text/50">mapped {map.mapped_on}</span>
      </div>
    </Card>
  );
}
